import { fetchLandingSites } from '@/api/landings';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { ILandingSite } from '@/entities/landing.entity';
import { PageScroll } from '@/layouts/page-layout';
import AddSiteModal from '@/pages/landings/components/add-site-modal';
import SiteTelegramAuthToggle from '@/pages/landings/components/site-telegram-auth-toggle';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import LandingsPageCard from '@/pages/landings/components/landings-page-card';
import LandingsGate from '@/pages/landings/landings-gate';
import { LayoutTemplateIcon, PlusIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

function formatSitesCount(count: number): string {
	if (count === 0) return 'Нет сайтов';
	const word = count === 1 ? 'сайт' : count < 5 ? 'сайта' : 'сайтов';
	return `${count} ${word}`;
}

export default function LandingsSitesPage() {
	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<LandingsGate>
				<SitesContent />
			</LandingsGate>
		</PageScroll>
	);
}

function SitesContent() {
	const [sites, setSites] = useState<ILandingSite[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [addOpen, setAddOpen] = useState(false);

	const loadSites = useCallback(async (showSpinner = false) => {
		if (showSpinner) setLoading(true);
		setError(false);
		try {
			const data = await fetchLandingSites();
			setSites(data);
		} catch {
			setError(true);
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadSites(true);
	}, [loadSites]);

	return (
		<LandingsPageCard
			icon={LayoutTemplateIcon}
			title='Сайты'
			description={formatSitesCount(sites.length)}
			color='chart-4'
			action={
				<Button type='button' size='sm' onClick={() => setAddOpen(true)}>
					<PlusIcon size={16} className='mr-1' />
					Создать
				</Button>
			}
		>
			{loading ? (
				<div className='flex justify-center py-8'>
					<Spinner />
				</div>
			) : error ? (
				<LandingsErrorState onRetry={() => void loadSites(true)} />
			) : sites.length === 0 ? (
				<LandingsEmptyState
					icon={LayoutTemplateIcon}
					title='Сайтов пока нет'
					description='Создайте сайт на онлайн-домене и выберите шаблон лендинга.'
					actionLabel='Создать сайт'
					onAction={() => setAddOpen(true)}
				/>
			) : (
				<div className='flex flex-col gap-3'>
					{sites.map(site => (
						<div key={site.id} className='rounded-xl border border-border bg-card p-3 shadow-sm'>
							<div className='flex items-start gap-3'>
								<div className='rounded-lg bg-chart-4/15 p-2 shrink-0'>
									<LayoutTemplateIcon className='text-chart-4' size={16} />
								</div>
								<div className='min-w-0 flex-1'>
									<p className='font-medium truncate'>{site.fullHostname}</p>
									<div className='mt-1.5 flex flex-wrap items-center gap-2'>
										<span className='inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs text-muted-foreground'>
											{site.templateSlug}
										</span>
										<span className='text-xs text-muted-foreground'>
											{new Date(site.createdAt).toLocaleDateString('ru-RU')}
										</span>
									</div>
									{site.templateSlug === 'telegram-auth' ? (
										<SiteTelegramAuthToggle site={site} onUpdated={() => void loadSites()} />
									) : null}
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			<AddSiteModal open={addOpen} onOpenChange={setAddOpen} onCreated={() => void loadSites()} />
		</LandingsPageCard>
	);
}
